/* eslint-disable import/no-cycle */
import CryptoJS from 'crypto-js';
import generateVersionHash from '@utils/generateVersionHash';
import { attempt, createStoreFetcher } from '@/lib/http';
import { btoa, atob, showAlert } from '../../lib/helpers';
import { dataIntervalExport } from '../params';
import { postApi } from '../../lib/fetcher';
import api, { ROLE } from './constants';

// HO = Approver 2
const ho = {
  namespaced: true,

  state() {
    return {
      allMerchants: [],
      totalMerchants: 0,
      allMerchantsHistory: [],
      totalMerchantsHistory: 0,
      loading: false,
      errors: [],
      flashErrorMessage: '',
      flashError: false,
      merchant: null,
      merchantHistory: null,
      totalPage: 0,
      intervalExport: null,
      exportId: null,
      countDataExport: 0,
    };
  },

  mutations: {
    setMerchants(state, { payload }) {
      state.allMerchants = payload.data.list;
    },

    setMerchantsHistory(state, { payload }) {
      state.allMerchantsHistory = payload.data.list;
    },

    setFlashErrorMessage(state, payload) {
      state.flashErrorMessage = payload;
      state.flashError = payload != '';
    },

    clearMerchants(state) {
      state.allMerchants = [];
      state.totalMerchants = 0;
    },

    clearErrors(state) {
      state.errors = [];
    },

    setErrors(state, payload) {
      state.errors = payload;
    },

    setTotalMerchants(state, { payload }) {
      state.totalMerchants = parseInt(payload.data.countdata, 10);
      state.totalPage = parseInt(payload.data.totalPage, 10);
    },

    setTotalMerchantsHistory(state, { payload }) {
      state.totalMerchantsHistory = parseInt(payload.data.countdata, 10);
      state.totalPage = parseInt(payload.data.totalPage, 10);
    },

    setLoading(state, loading) {
      state.loading = loading;
    },

    setDetailMerchant(state, { payload }) {
      state.merchant = payload;
    },

    setDetailMerchantHistory(state, { payload }) {
      state.merchantHistory = payload;
    },

    setIntervalExport(state, payload) {
      state.intervalExport = payload;
    },

    setExportData(state, payload) {
      state.exportId = payload.export_id;
      state.countDataExport = payload.count_data;
    },

    setStateVariable(state, { payload, variable }) {
      state[variable] = payload;
    },
  },

  actions: {
    async fetchMerchants(
      ctx,
      { date, page, itemsPerPage, sortBy, sortDesc, search }
    ) {
      ctx.commit('clearMerchants');
      ctx.commit('setLoading', true);
      const sort = !sortDesc[0] ? 'ASC' : 'DESC';
      let sort_by = '';
      if (sortBy[0] != null) {
        if (sortBy[0] == 'mid') {
          sort_by = `mid_static ${sort}`;
        } else {
          sort_by = `${sortBy[0]} ${sort}`;
        }
      } else {
        sort_by = `id ${sort}`;
      }

      const data = JSON.stringify({
        search,
        sort_by,
        limit: itemsPerPage.toString(),
        page: page.toString(),
        version: btoa(
          CryptoJS.SHA256(
            itemsPerPage.toString() +
              page.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const urlApi = `/index.php?r=api/v1/request_list/approver-2/index`;

      const responseData = await postApi(ctx, urlApi, data);

      if (responseData) {
        ctx.commit('setTotalMerchants', {
          payload: responseData,
        });
        ctx.commit('setMerchants', { payload: responseData });
      }
    },

    async viewMerchant(ctx, { date, id }) {
      ctx.commit('setLoading', true);
      const data = JSON.stringify({
        id,
        version: btoa(
          CryptoJS.SHA256(
            id.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });
      const urlApi = `/index.php?r=api/v1/request_list/approver-2/view`;

      const responseData = await postApi(ctx, urlApi, data);

      if (responseData?.code == '200') {
        ctx.commit('setDetailMerchant', {
          payload: responseData.data,
        });
      }
    },

    async fetchMerchantsHistory(
      ctx,
      { date, page, itemsPerPage, sortBy, sortDesc, search }
    ) {
      ctx.commit('setLoading', true);
      const sort = !sortDesc[0] ? 'DESC' : 'ASC';
      let sort_by = '';
      if (sortBy[0] != null) {
        if (sortBy[0] == 'mid') {
          sort_by = `mid_static ${sort}`;
        } else {
          sort_by = `${sortBy[0]} ${sort}`;
        }
      } else {
        sort_by = `id ${sort}`;
      }

      const data = JSON.stringify({
        search,
        sort_by,
        limit: itemsPerPage.toString(),
        page: page.toString(),
        version: btoa(
          CryptoJS.SHA256(
            itemsPerPage.toString() +
              page.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const urlApi = `/index.php?r=api/v1/request_list/approver-2/history`;

      const responseData = await postApi(ctx, urlApi, data);

      if (responseData) {
        ctx.commit('setTotalMerchantsHistory', {
          payload: responseData,
        });
        ctx.commit('setMerchantsHistory', { payload: responseData });
      }
    },

    async viewMerchantHistory(ctx, { date, id }) {
      ctx.commit('setLoading', true);
      const data = JSON.stringify({
        id,
        version: btoa(
          CryptoJS.SHA256(
            id.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });
      const urlApi = `/index.php?r=api/v1/request_list/approver-2/history-view`;

      const responseData = await postApi(ctx, urlApi, data);

      if (responseData?.code == '200') {
        ctx.commit('setDetailMerchantHistory', {
          payload: responseData.data,
        });
        return responseData.data;
      }
      return null;
    },

    /**
     * Approves several submissions in one request.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @param {Array} params.id - Submissions to approve.
     * @returns {Promise<boolean>} True when the batch was accepted.
     *
     * The server reports the outcome of the batch in `message`, including the
     * case where only part of it succeeded, so that message is shown as sent
     * rather than a fixed wording chosen by the screen.
     */
    async bulk_approve(ctx, { date, id }) {
      const account = JSON.parse(
        localStorage.getItem('logged_in_qram') || '{}'
      );
      const remark = `Bulk Approve by ${account?.user?.username || ''}`;
      const http = createStoreFetcher(ctx);

      return attempt(async () => {
        const res = await http.post(api.bulkApprove(ROLE.APPROVER_2), {
          merchant_id: id,
          remark,
          version: generateVersionHash('', date),
        });

        showAlert(
          ctx,
          res.message || 'Approve success !',
          'mdi-check-circle',
          'green'
        );

        return true;
      }, false);
    },

    /**
     * Approves or rejects one submission.
     *
     * The decision is the trailing route segment, which is how the screens have
     * always been wired.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @param {string|number} params.id - Submission being decided.
     * @param {string} params.category - Route segment naming the decision.
     * @param {string} params.remark - Reason entered by the approver.
     * @returns {Promise<boolean>} True when the decision was recorded.
     */
    async approve_reject(ctx, { date, id, category, remark }) {
      const http = createStoreFetcher(ctx);

      return attempt(async () => {
        await http.post(api.decision(ROLE.APPROVER_2, category), {
          id,
          remark,
          version: generateVersionHash(id.toString(), date),
        });

        return true;
      }, false);
    },

    /**
     * Sends a submission back to the previous step for revision.
     *
     * @param {Object} ctx - Vuex action context.
     * @param {Object} params - Action payload.
     * @param {string} params.date - Timestamp used by the version hash.
     * @param {string|number} params.id - Submission being sent back.
     * @param {string} params.remark - Reason entered by the approver.
     * @returns {Promise<boolean>} True when the submission was sent back.
     */
    async sendback(ctx, { date, id, remark }) {
      const http = createStoreFetcher(ctx);

      return attempt(async () => {
        await http.post(api.sendBack(ROLE.APPROVER_2), {
          id,
          remark,
          version: generateVersionHash(id.toString() + remark.toString(), date),
        });

        return true;
      }, false);
    },

    async exportList(
      ctx,
      { date, page, itemsPerPage, sortBy, sortDesc, search, view }
    ) {
      if (ctx.rootState.isExport) {
        ctx.commit(
          'setSnackbar',
          {
            status: true,
            message: 'Export in progress !',
            icon: 'mdi-alert',
            color: 'red',
          },
          { root: true }
        );
        return;
      }
      ctx.commit('setLoading', true);
      ctx.commit('setIsExport', true, { root: true });
      ctx.commit('setStateVariable', {
        payload: 0,
        variable: 'countSameExport',
      });
      const sort = !sortDesc[0] ? 'ASC' : 'DESC';
      let sort_by = '';
      if (sortBy[0] != null) {
        sort_by = `${sortBy[0]} ${sort}`;
      } else {
        sort_by = `id ${sort}`;
      }
      const controller = (ctrl) => {
        switch (ctrl) {
          case 'ho-history':
            return 'export-history-ho-to-do-list';
          case 'ho':
            return 'export-ho-to-do-list';
          default:
            return 'export-ho-to-do-list';
        }
      };

      const data = JSON.stringify({
        search,
        sort_by,
        limit: itemsPerPage.toString(),
        page: page.toString(),
        version: btoa(
          CryptoJS.SHA256(
            itemsPerPage.toString() +
              page.toString() +
              date +
              atob(process.env.VUE_APP_STRING_EXTRA1.slice(0, -3))
          ).toString()
        ),
      });

      const failed = () => {
        ctx.commit(
          'setSnackbar',
          {
            status: true,
            message: 'Export Failed !',
            icon: 'mdi-alert',
            color: 'red',
          },
          { root: true }
        );
        ctx.commit(
          'setInsertProgressGlobal',
          {
            absoluteBar: false,
            absoluteBarText: '',
          },
          { root: true }
        );
        ctx.commit('setIsExport', false, { root: true });
      };

      const urlApi = `/index.php?r=api/v1/export/${controller(view)}/index`;

      const responseData = await postApi(ctx, urlApi, data);

      if (responseData?.code == '200') {
        if (responseData.data.message != null) {
          showAlert(ctx, responseData.data.message, 'mdi-alert', 'red');
          ctx.commit('setIsExport', false, { root: true });
          return;
        }
        ctx.commit('setExportData', responseData.data);
        const interval = setInterval(() => {
          ctx.dispatch(
            'exportStatus',
            {
              date,
              exportId: ctx.state.exportId,
              countDataExport: ctx.state.countDataExport,
              controller: controller(view),
            },
            { root: true }
          );
        }, dataIntervalExport);
        ctx.commit('setIntervalExport', interval, { root: true });
        ctx.commit(
          'setInsertProgressGlobal',
          {
            absoluteBar: true,
            absoluteBarText: 'Start to export data..',
          },
          { root: true }
        );
      } else {
        failed();
      }
    },
  },
};
export default ho;
